import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Wallet,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowUpRight,
  Loader2,
  TrendingUp,
  Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';

const MIN_WITHDRAWAL = 5; // AUD

export default function WalletPage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const [balance, setBalance] = useState(null);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setupLoading, setSetupLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');

  useEffect(() => {
    // Handle Stripe Connect return/refresh
    const setup = searchParams.get('setup');
    if (setup === 'complete') toast.success('Stripe account setup complete!');
    if (setup === 'refresh') toast.info('Setup link expired — refreshing...');

    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [balRes, statusRes, histRes] = await Promise.all([
        api.get('/wallet/balance'),
        api.get('/wallet/status'),
        api.get('/wallet/history', { params: { limit: 20 } }),
      ]);
      setBalance(balRes.data);
      setStatus(statusRes.data);
      setHistory(histRes.data.transactions || []);
    } catch {
      toast.error('Failed to load wallet');
    } finally {
      setLoading(false);
    }
  }

  async function handleSetup() {
    setSetupLoading(true);
    try {
      const r = await api.post('/wallet/setup');
      window.location.href = r.data.onboarding_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start setup');
    } finally {
      setSetupLoading(false);
    }
  }

  async function handleWithdraw() {
    const cents = Math.round(parseFloat(withdrawAmount) * 100);
    if (!cents || cents < 500) {
      toast.error(`Minimum withdrawal is $${MIN_WITHDRAWAL}`);
      return;
    }
    setWithdrawLoading(true);
    try {
      const r = await api.post('/wallet/withdraw', { amount_cents: cents });
      toast.success(`Withdrawal of $${r.data.amount_aud} initiated`);
      setWithdrawAmount('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawLoading(false);
    }
  }

  const handleDownloadInvoice = async (transactionId) => {
    try {
      const response = await api.get(`/transactions/${transactionId}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `WANTD-Invoice-${transactionId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const payoutsEnabled = status?.payouts_enabled;
  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] font-['Manrope']">
            Seller Wallet
          </h1>
          <p className="text-sm text-[var(--theme-text-muted)] mt-0.5">
            Your earnings from completed sales.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--theme-text-muted)]" />
          </div>
        ) : (
          <>
            {/* Balance card */}
            <div className="p-5 rounded-2xl border border-[var(--theme-border)] bg-[var(--theme-card)] mb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#1E3A5F] flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-[var(--theme-text-muted)]">Available Balance</p>
                  <p className="text-2xl font-bold text-[var(--theme-text-primary)] font-['Manrope']">
                    {fmt(balance?.balance_cents ?? 0)}
                  </p>
                </div>
              </div>

              {/* Stripe Connect status banner */}
              {status?.status === 'not_started' && (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 mb-4">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-800 font-['Manrope']">
                      Set up payouts
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Connect your bank account to withdraw earnings.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleSetup}
                    disabled={setupLoading}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-full text-xs flex-shrink-0"
                  >
                    {setupLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                      <><ExternalLink className="w-3 h-3 mr-1" />Set up</>
                    )}
                  </Button>
                </div>
              )}

              {status?.status === 'onboarding' && (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 border border-blue-200 mb-4">
                  <Clock className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-800 font-['Manrope']">Setup in progress</p>
                    <p className="text-xs text-blue-700 mt-0.5">Complete your Stripe onboarding to enable payouts.</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleSetup}
                    disabled={setupLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs flex-shrink-0"
                  >
                    {setupLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Continue'}
                  </Button>
                </div>
              )}

              {payoutsEnabled && (
                <div className="flex items-center gap-2 mb-4 text-xs text-[#059669]">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-semibold font-['Manrope']">Payouts enabled</span>
                </div>
              )}

              {/* Withdrawal form */}
              {payoutsEnabled && (balance?.balance_cents ?? 0) >= 500 && (
                <div className="border-t border-[var(--theme-border)] pt-4">
                  <p className="text-sm font-semibold text-[var(--theme-text-primary)] mb-2 font-['Manrope']">
                    Withdraw funds
                  </p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)] text-sm">
                        $
                      </span>
                      <input
                        type="number"
                        min={MIN_WITHDRAWAL}
                        max={(balance?.balance_cents ?? 0) / 100}
                        step="0.01"
                        value={withdrawAmount}
                        onChange={e => setWithdrawAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-7 pr-3 py-2 rounded-xl border border-[var(--theme-border)]
                                   bg-[var(--theme-input)] text-[var(--theme-text-primary)] text-sm
                                   focus:outline-none focus:border-[#1E3A5F]"
                      />
                    </div>
                    <Button
                      onClick={handleWithdraw}
                      disabled={withdrawLoading || !withdrawAmount}
                      className="rounded-full bg-[#1E3A5F] hover:bg-[#16304f] text-white px-4"
                    >
                      {withdrawLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><ArrowUpRight className="w-4 h-4 mr-1" />Withdraw</>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-[var(--theme-text-muted)] mt-1.5">
                    Min ${MIN_WITHDRAWAL} — transfers usually arrive in 2–7 business days.
                  </p>
                </div>
              )}
            </div>

            {/* Transaction history */}
            <h2 className="text-base font-bold text-[var(--theme-text-primary)] font-['Manrope'] mb-3">
              Recent Sales
            </h2>
            {history.length === 0 ? (
              <div className="text-center py-10">
                <TrendingUp className="w-8 h-8 mx-auto text-[var(--theme-text-muted)] mb-2" />
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  No completed sales yet.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map(tx => (
                  <div
                    key={tx.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-[var(--theme-card)]
                               border border-[var(--theme-border)]"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 text-[#059669]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--theme-text-primary)] font-['Manrope'] truncate">
                        {tx.item_title || 'Sale'}
                      </p>
                      <p className="text-xs text-[var(--theme-text-muted)]">
                        {tx.completed_at
                          ? new Date(tx.completed_at).toLocaleDateString('en-AU', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })
                          : '—'}
                      </p>
                    </div>
                    <p className="text-sm font-bold text-[#059669] flex-shrink-0">
                      {fmt(tx.seller_credited_cents)}
                    </p>
                    <button
                      onClick={() => handleDownloadInvoice(tx.id)}
                      className="p-1.5 rounded-lg text-[var(--theme-text-muted)] hover:text-[#1E3A5F]
                                 hover:bg-[var(--theme-hover)] transition-colors flex-shrink-0"
                      title="Download fee invoice"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
